/** Canonical ACP v2 entities, independent of provider and transport lifecycle. */
import type {
  AgentCapabilities,
  AgentMessage,
  AvailableCommand,
  CompactionUpdate,
  ContentBlock,
  CreateElicitationRequest,
  PlanUpdateContent,
  SessionConfigOption,
  SessionInfoUpdate,
  SessionUpdate,
  TerminalUpdate,
  ToolCallUpdate,
  Usage,
  UsageUpdate,
} from '@agentclientprotocol/sdk/experimental/v2'
import type { ThreadMessageLike } from '@assistant-ui/react'
import {
  appToolReference,
  declaredPresentation,
  normalizedOrigin,
  outputText,
  verifiedResult,
} from '@bridge/web/tool-projection'
import type {
  ToolArtifact,
  ToolResultPayload,
} from '@web/adapter/toThreadMessages'
import type {
  AppToolPresentation,
  MessageSendMode,
  UIInteraction,
} from '@web/api/types'
import type { BridgeState } from '@web/state/bridge'
import { withLifecycleMessages } from '@web/state/lifecycle'
import {
  type MessageActor,
  readMessageActor,
  type ToolPart,
} from 'cc-render/model'

type Update<K extends string> = Extract<SessionUpdate, { sessionUpdate: K }>
type Position = {
  seq: number
  updatedSeq: number
  requestId?: string
  timestamp?: number
  updatedTimestamp?: number
  startedTimestamp?: number
  finishedTimestamp?: number
}
export type MessageEntity = AgentMessage &
  Position & { role: 'user' | 'assistant' | 'reasoning'; chunks: unknown[] }
export type ToolEntity = ToolCallUpdate &
  Position & { chunks: unknown[]; presentation?: AppToolPresentation }
export type TerminalEntity = TerminalUpdate &
  Position & { outputChunks: string[] }
export type ElicitationEntity = {
  requestId: string
  request: CreateElicitationRequest
  seq: number
  resolved?: boolean
}
export type ConversationProvenance = {
  version: 1
  sessionId: string
  parentSessionId: string
  parentToolCallId?: string
  name?: string
  state?: 'running' | 'idle'
  context?: { used: number; size: number }
}
/** Read the advertised, provider-neutral child conversation identity. */
export function conversationProvenance(
  meta: unknown,
): ConversationProvenance | undefined {
  const value = object(object(object(meta)['dev.agentbridge']).conversation)
  if (
    value.version !== 1 ||
    typeof value.sessionId !== 'string' ||
    typeof value.parentSessionId !== 'string'
  )
    return undefined
  return value as ConversationProvenance
}

export type AgentMetadata = {
  version?: number
  workspace?: boolean
  artifacts?: boolean
  executionMode?: string
  costBasis?: 'provider_reported' | 'reported' | 'not_reported'
  notifications?: Record<
    string,
    { schema: Record<string, unknown>; presentation: string }
  >
  extensions?: Record<
    string,
    {
      version: number
      phase?: 'before_session' | 'after_session'
      requestSchema: Record<string, unknown>
      responseSchema: Record<string, unknown>
    }
  >
}
export type AcpState = {
  active: boolean
  conversations: Record<string, ConversationProvenance>
  providerSessionId: string | null
  info: SessionInfoUpdate
  foreground: 'running' | 'requires_action' | 'idle'
  running: boolean
  stopReason: string | null
  turn: string | null
  messages: Record<string, MessageEntity>
  tools: Record<string, ToolEntity>
  plans: Record<string, PlanUpdateContent>
  terminals: Record<string, TerminalEntity>
  compactions: Record<string, CompactionUpdate & Position>
  timeline: { kind: 'message' | 'tool' | 'compaction'; id: string }[]
  configOptions: SessionConfigOption[]
  capabilities: AgentCapabilities
  commands: AvailableCommand[]
  elicitations: Record<string, ElicitationEntity>
  usage: UsageUpdate | null
  turnUsage: Usage | null
  costs: { seq: number; cost: NonNullable<UsageUpdate['cost']> }[]
  extensions: { seq: number; payload: Record<string, unknown> }[]
  toolResults: Record<string, Record<string, any>>
}
export function initialAcpState(): AcpState {
  return {
    active: false,
    conversations: {},
    providerSessionId: null,
    info: {},
    foreground: 'idle',
    running: false,
    stopReason: null,
    turn: null,
    messages: {},
    tools: {},
    plans: {},
    terminals: {},
    compactions: {},
    timeline: [],
    configOptions: [],
    capabilities: {},
    commands: [],
    elicitations: {},
    usage: null,
    turnUsage: null,
    costs: [],
    extensions: [],
    toolResults: {},
  }
}
export function agentMetadata(state: AcpState): AgentMetadata {
  const value = state.capabilities._meta?.['dev.agentbridge']
  return value && typeof value === 'object' ? (value as AgentMetadata) : {}
}
const object = (value: unknown): Record<string, any> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, any>)
    : {}
const position = (
  seq: number,
  requestId?: string,
  timestamp?: number,
): Position => ({
  seq,
  updatedSeq: seq,
  timestamp,
  updatedTimestamp: timestamp,
  requestId,
})

/** Values replace, explicit null clears, and omitted properties remain unchanged. */
function patch<T extends object>(current: T, update: object): T {
  return {
    ...current,
    ...Object.fromEntries(
      Object.entries(update).filter(
        ([key, value]) => key !== 'sessionUpdate' && value !== undefined,
      ),
    ),
  }
}
function ordered(
  state: AcpState,
  kind: AcpState['timeline'][number]['kind'],
  id: string,
): AcpState['timeline'] {
  return state.timeline.some((item) => item.kind === kind && item.id === id)
    ? state.timeline
    : [...state.timeline, { kind, id }]
}

/** Freeze a trace's duration at its real transition, including turns without an answer. */
function finishThoughts(
  messages: AcpState['messages'],
  timestamp: number | undefined,
  conversationId?: string,
  exceptId?: string,
): AcpState['messages'] {
  if (timestamp === undefined) return messages
  let result = messages
  for (const [id, message] of Object.entries(messages)) {
    if (
      message.role !== 'reasoning' ||
      id === exceptId ||
      message.finishedTimestamp !== undefined ||
      conversationProvenance(message._meta)?.sessionId !== conversationId
    )
      continue
    if (result === messages) result = { ...messages }
    result[id] = { ...message, finishedTimestamp: timestamp }
  }
  return result
}

/** Fold validated v2 updates; background entity activity never changes foreground state. */
export function applyAcpFrame(
  state: AcpState,
  frame: Record<string, any>,
  seq: number,
  timestamp?: number,
): AcpState {
  if (frame.type === 'agent/batch' && Array.isArray(frame.frames))
    return frame.frames.reduce(
      (current: AcpState, child: Record<string, any>) =>
        applyAcpFrame(current, child, seq, timestamp),
      state,
    )
  if (
    frame.type === 'bridge/app_action' &&
    typeof frame.invocation_id === 'string' &&
    typeof frame.title === 'string' &&
    ['in_progress', 'completed', 'failed', 'cancelled'].includes(frame.status)
  ) {
    const next = applyAcpFrame(
      state,
      {
        type: 'agent/update',
        request_id: `app:${frame.invocation_id}`,
        update: {
          sessionUpdate: 'tool_call_update',
          toolCallId: `app:${frame.invocation_id}`,
          name: 'AppAction',
          title: frame.title,
          status: frame.status,
          ...(frame.status !== 'in_progress'
            ? { rawOutput: frame.detail }
            : {}),
        },
      },
      seq,
      timestamp,
    )
    // App work can overlap an agent thought without ending that thought.
    return { ...next, messages: state.messages }
  }
  if (frame.type === 'agent/initialize')
    return {
      ...state,
      active: true,
      capabilities: frame.response?.capabilities ?? {},
    }
  if (frame.type === 'agent/session')
    return {
      ...state,
      active: true,
      providerSessionId: frame.provider_session_id ?? state.providerSessionId,
      capabilities: frame.capabilities ?? state.capabilities,
      configOptions: frame.config_options ?? state.configOptions,
    }
  if (frame.type === 'bridge/hello' || frame.type === 'bridge/record') {
    const config = frame.session?.config
    return config?.protocol === 'acp'
      ? {
          ...state,
          active: true,
          providerSessionId:
            config.provider_session_id ?? state.providerSessionId,
        }
      : state
  }
  if (frame.type === 'bridge/prompt_started')
    return { ...state, turn: frame.request_id ?? state.turn }
  if (
    frame.type === 'bridge/tool_result' &&
    frame.verified === true &&
    typeof frame.tool_call_id === 'string'
  )
    return {
      ...state,
      toolResults: { ...state.toolResults, [frame.tool_call_id]: frame },
    }
  if (frame.type === 'bridge/elicitation_request') {
    const id = String(frame.request_id)
    return {
      ...state,
      elicitations: {
        ...state.elicitations,
        [id]: { requestId: id, request: frame.request ?? frame, seq },
      },
    }
  }
  if (frame.type === 'bridge/elicitation_resolved') {
    const previous = state.elicitations[frame.request_id]
    return previous
      ? {
          ...state,
          elicitations: {
            ...state.elicitations,
            [frame.request_id]: { ...previous, resolved: true },
          },
        }
      : state
  }
  if (frame.type !== 'agent/update') {
    return frame.type?.startsWith('agent/')
      ? { ...state, extensions: [...state.extensions, { seq, payload: frame }] }
      : state
  }
  const update = object(frame.update) as SessionUpdate
  const kind = update.sessionUpdate
  if (typeof kind !== 'string') return state
  const next = { ...state, active: true }
  const conversation = conversationProvenance(update._meta)
  if (conversation) {
    next.conversations = {
      ...state.conversations,
      [conversation.sessionId]: {
        ...state.conversations[conversation.sessionId],
        ...conversation,
      },
    }
    if (kind === 'state_update') {
      const value = update as Update<'state_update'>
      if (value.state !== 'running')
        next.messages = finishThoughts(
          next.messages,
          timestamp,
          conversation.sessionId,
        )
      next.conversations[conversation.sessionId] = {
        ...next.conversations[conversation.sessionId]!,
        state: value.state === 'idle' ? 'idle' : 'running',
      }
      return next
    }
    if (
      [
        'session_info_update',
        'usage_update',
        'config_option_update',
        'available_commands_update',
      ].includes(kind)
    ) {
      next.extensions = [...state.extensions, { seq, payload: update }]
      return next
    }
  }
  const requestId =
    typeof frame.request_id === 'string'
      ? frame.request_id
      : (state.turn ?? undefined)
  if (
    [
      'user_message',
      'agent_message',
      'agent_thought',
      'user_message_chunk',
      'agent_message_chunk',
      'agent_thought_chunk',
    ].includes(kind)
  ) {
    const value = update as
      | Update<'agent_message'>
      | Update<'agent_message_chunk'>
    if (typeof value.messageId !== 'string') return state
    const role = kind.startsWith('user_')
      ? 'user'
      : kind.startsWith('agent_thought')
        ? 'reasoning'
        : 'assistant'
    const previous = state.messages[value.messageId] ?? {
      messageId: value.messageId,
      role,
      content: [],
      chunks: [],
      ...position(seq, requestId, timestamp),
    }
    const message = kind.endsWith('_chunk')
      ? {
          ...previous,
          content: [...(previous.content ?? []), value.content as ContentBlock],
          chunks: [...previous.chunks, value],
          updatedSeq: seq,
          updatedTimestamp: timestamp,
        }
      : {
          ...patch(previous, value),
          chunks: Object.hasOwn(value, 'content') ? [] : previous.chunks,
          updatedSeq: seq,
          updatedTimestamp: timestamp,
        }
    if (
      role === 'reasoning' &&
      (kind.endsWith('_chunk') ||
        JSON.stringify(message.content) !== JSON.stringify(previous.content))
    )
      message.finishedTimestamp = undefined
    // Empty answer placeholders do not precede thought traces in the visible timeline.
    if (
      role === 'assistant' &&
      !(previous.content ?? []).some(
        (block) => block.type !== 'text' || object(block).text?.trim(),
      ) &&
      (message.content ?? []).some(
        (block) => block.type !== 'text' || object(block).text?.trim(),
      )
    ) {
      message.seq = seq
      message.timestamp = timestamp
    }
    next.messages = { ...state.messages, [value.messageId]: message }
    if (
      (message.content ?? []).some(
        (block) => block.type !== 'text' || object(block).text?.trim(),
      )
    )
      next.messages = finishThoughts(
        next.messages,
        timestamp,
        conversation?.sessionId,
        role === 'reasoning' ? value.messageId : undefined,
      )
    next.timeline = ordered(state, 'message', value.messageId)
  } else if (
    kind === 'tool_call_update' ||
    kind === 'tool_call_content_chunk'
  ) {
    const value = update as
      | Update<'tool_call_update'>
      | Update<'tool_call_content_chunk'>
    if (typeof value.toolCallId !== 'string') return state
    const previous = state.tools[value.toolCallId] ?? {
      toolCallId: value.toolCallId,
      chunks: [],
      ...position(seq, requestId, timestamp),
    }
    const tool =
      kind === 'tool_call_content_chunk'
        ? {
            ...previous,
            content: [
              ...(previous.content ?? []),
              (value as Update<'tool_call_content_chunk'>).content,
            ],
            chunks: [...previous.chunks, value],
            updatedSeq: seq,
            updatedTimestamp: timestamp,
          }
        : {
            ...patch(previous, value),
            chunks: Object.hasOwn(value, 'content') ? [] : previous.chunks,
            updatedSeq: seq,
            updatedTimestamp: timestamp,
          }
    const observedStatus =
      kind === 'tool_call_update' ? object(value).status : undefined
    if (
      observedStatus === 'in_progress' &&
      tool.startedTimestamp === undefined &&
      timestamp !== undefined
    )
      tool.startedTimestamp = timestamp
    if (
      ['completed', 'failed', 'cancelled', 'interrupted'].includes(
        String(observedStatus),
      ) &&
      tool.finishedTimestamp === undefined &&
      timestamp !== undefined
    )
      tool.finishedTimestamp = timestamp
    next.tools = { ...state.tools, [value.toolCallId]: tool }
    next.messages = finishThoughts(
      next.messages,
      timestamp,
      conversation?.sessionId,
    )
    next.timeline = ordered(state, 'tool', value.toolCallId)
  } else if (kind === 'state_update') {
    const value = update as Update<'state_update'>
    if (!['running', 'requires_action', 'idle'].includes(value.state))
      return {
        ...next,
        extensions: [...state.extensions, { seq, payload: update }],
      }
    next.foreground = value.state as AcpState['foreground']
    next.running = value.state !== 'idle'
    next.stopReason =
      value.state === 'idle' && typeof value.stopReason === 'string'
        ? value.stopReason
        : null
    if (value.state !== 'running')
      next.messages = finishThoughts(next.messages, timestamp)
    if (value.state === 'idle')
      next.turnUsage = (value.usage ?? null) as Usage | null
  } else if (kind === 'terminal_update' || kind === 'terminal_output_chunk') {
    const value = update as
      | Update<'terminal_update'>
      | Update<'terminal_output_chunk'>
    if (typeof value.terminalId !== 'string') return state
    const previous = state.terminals[value.terminalId] ?? {
      terminalId: value.terminalId,
      outputChunks: [],
      ...position(seq, requestId, timestamp),
    }
    const terminal =
      kind === 'terminal_output_chunk'
        ? {
            ...previous,
            outputChunks: [
              ...previous.outputChunks,
              (value as Update<'terminal_output_chunk'>).data,
            ],
            updatedSeq: seq,
            updatedTimestamp: timestamp,
          }
        : {
            ...patch(previous, value),
            outputChunks: Object.hasOwn(value, 'output')
              ? []
              : previous.outputChunks,
            updatedSeq: seq,
            updatedTimestamp: timestamp,
          }
    next.terminals = { ...state.terminals, [value.terminalId]: terminal }
  } else if (kind === 'plan_update') {
    const value = update as Update<'plan_update'>
    if (typeof value.plan?.planId !== 'string') return state
    next.plans = { ...state.plans, [value.plan.planId]: value.plan }
  } else if (kind === 'plan_removed') {
    next.plans = { ...state.plans }
    delete next.plans[(update as Update<'plan_removed'>).planId]
  } else if (kind === 'session_info_update')
    next.info = patch(state.info, update)
  else if (kind === 'config_option_update')
    next.configOptions = (
      update as Update<'config_option_update'>
    ).configOptions
  else if (kind === 'available_commands_update')
    next.commands = (
      update as Update<'available_commands_update'>
    ).availableCommands
  else if (kind === 'usage_update') {
    const value = update as Update<'usage_update'>
    next.usage = value
    if (value.cost) next.costs = [...state.costs, { seq, cost: value.cost }]
  } else if (
    kind === 'compaction_update' ||
    kind === 'compaction_summary_chunk'
  ) {
    const value = update as
      | Update<'compaction_update'>
      | Update<'compaction_summary_chunk'>
    const previous = state.compactions[value.compactionId] ?? {
      compactionId: value.compactionId,
      status: 'in_progress',
      summary: [],
      seq,
      updatedSeq: seq,
    }
    const compaction =
      kind === 'compaction_summary_chunk'
        ? {
            ...previous,
            summary: [
              ...(previous.summary ?? []),
              (value as Update<'compaction_summary_chunk'>).content,
            ],
          }
        : patch(previous, value)
    next.compactions = {
      ...state.compactions,
      [value.compactionId]: {
        ...compaction,
        seq: previous.seq ?? seq,
        updatedSeq: seq,
      },
    }
    next.timeline = ordered(state, 'compaction', value.compactionId)
  } else next.extensions = [...state.extensions, { seq, payload: update }]
  return next
}

/** Decode all terminal bytes together so split UTF-8 codepoints survive chunks. */
export function terminalText(terminal: TerminalEntity): string {
  try {
    const parts = [terminal.output?.data ?? '', ...terminal.outputChunks].map(
      (value) => Uint8Array.from(atob(value), (c) => c.charCodeAt(0)),
    )
    const joined = new Uint8Array(
      parts.reduce((length, part) => length + part.length, 0),
    )
    let offset = 0
    for (const part of parts) {
      joined.set(part, offset)
      offset += part.length
    }
    return new TextDecoder().decode(joined)
  } catch {
    return 'Terminal output could not be decoded.'
  }
}

type ToolStatus = NonNullable<ToolArtifact['status']>
type AcpItem = {
  id: string
  seq?: number
  updatedSeq?: number
  timestamp?: number
  updatedTimestamp?: number
  startedTimestamp?: number
  finishedTimestamp?: number
  role: 'user' | 'assistant'
  actor?: MessageActor
  conversation?: ConversationProvenance
  requestId?: string
  origin?: string
  sendMode?: MessageSendMode
  interaction?: UIInteraction
  displayContent?: Record<string, any>[]
  text: string
  error?: string
  reasoning?: string
  tool?: {
    id: string
    name?: string
    title: string
    kind?: string
    input: unknown
    output?: unknown
    content?: unknown
    locations?: { path: string; line?: number }[]
    status: ToolStatus
    presentation?: AppToolPresentation
  }
}
const statuses: Record<string, ToolStatus> = {
  pending: 'pending',
  in_progress: 'running',
  completed: 'done',
  failed: 'error',
  cancelled: 'cancelled',
  interrupted: 'cancelled',
}
export const STOPPED_NOTICE = 'You stopped this response.'
const interruptionNotices = new Set([
  '[Request interrupted by user]',
  '[Request interrupted by user for tool use]',
])

/** Mirror bridge_session_model.is_interruption_notice: only the bare provider marker. */
export function isInterruptionNotice(content: unknown): boolean {
  if (!Array.isArray(content) || content.length !== 1) return false
  const block = object(content[0])
  return (
    block.type === 'text' &&
    interruptionNotices.has(String(block.text ?? '').trim())
  )
}

function presentationItems(state: AcpState): AcpItem[] {
  return state.timeline.flatMap<AcpItem>((entry) => {
    if (entry.kind === 'message') {
      const value = state.messages[entry.id]!
      const meta = object(value._meta?.['dev.agentbridge'])
      if (
        value.role === 'user' &&
        ['steer', 'send_now'].includes(meta.interruptionReason)
      )
        return []
      // A plain Stop leaves the provider's interruption marker in the transcript;
      // show it as a system notice rather than as something the user typed.
      const stopped =
        value.role === 'user' &&
        meta.interruptionReason === 'stop' &&
        isInterruptionNotice(value.content)
      const display = stopped
        ? [{ type: 'text', text: STOPPED_NOTICE }]
        : value.role === 'user' && Array.isArray(meta.displayContent)
          ? meta.displayContent
          : (value.content ?? [])
      const text = display
        .filter((block) => block.type === 'text')
        .map((block) => String(object(block).text ?? ''))
        .join('')
      // Adapters publish a failed turn as an agent message flagged with
      // error: true; present it as a failure rather than as model output.
      const failed = value.role === 'assistant' && meta.error === true
      return [
        {
          id: `message:${entry.id}`,
          seq: value.seq,
          updatedSeq: value.updatedSeq,
          timestamp: value.timestamp,
          updatedTimestamp: value.updatedTimestamp,
          finishedTimestamp: value.finishedTimestamp,
          role: value.role === 'user' ? 'user' : 'assistant',
          requestId: value.requestId,
          actor: readMessageActor(meta.actor),
          conversation: conversationProvenance(value._meta),
          origin: stopped ? 'system' : normalizedOrigin(meta.origin),
          sendMode: meta.sendMode,
          interaction: meta.interaction,
          text: value.role === 'reasoning' || failed ? '' : text,
          ...(failed
            ? {
                error: text.trim() || 'The agent could not complete this turn.',
              }
            : value.role === 'reasoning'
              ? { reasoning: text }
              : { displayContent: display }),
        },
      ]
    }
    if (entry.kind === 'compaction') {
      const value = state.compactions[entry.id]!
      return [
        {
          id: `compaction:${entry.id}`,
          seq: value.seq,
          updatedSeq: value.updatedSeq,
          timestamp: value.timestamp,
          updatedTimestamp: value.updatedTimestamp,
          finishedTimestamp: value.finishedTimestamp,
          role: 'assistant',
          text: outputText(value.summary ?? []),
          error: value.error ?? undefined,
        },
      ]
    }
    const value = state.tools[entry.id]!
    return [
      {
        id: `tool:${entry.id}`,
        seq: value.seq,
        updatedSeq: value.updatedSeq,
        timestamp: value.timestamp,
        updatedTimestamp: value.updatedTimestamp,
        startedTimestamp: value.startedTimestamp,
        finishedTimestamp: value.finishedTimestamp,
        role: 'assistant',
        conversation: conversationProvenance(value._meta),
        requestId: value.requestId,
        text: '',
        tool: {
          id: value.toolCallId,
          name: value.name ?? undefined,
          title: value.title ?? 'Agent tool',
          kind: value.kind ?? undefined,
          input: value.rawInput,
          output: value.rawOutput,
          content: value.content,
          locations: value.locations?.map((location) => ({
            path: location.path,
            line: location.line ?? undefined,
          })),
          status: statuses[value.status ?? 'pending'] ?? 'pending',
          presentation: value.presentation,
        },
      },
    ]
  })
}

/** Standard ACP kinds select presentation without inventing a provider name. */
const kindRenderer: Record<string, string> = {
  read: 'Read',
  edit: 'Edit',
  delete: 'Delete',
  move: 'Move',
  search: 'Grep',
  execute: 'Bash',
  fetch: 'WebFetch',
  think: 'Think',
  switch_mode: 'SwitchMode',
}

export function acpMessages(
  state: AcpState,
  bridge?: BridgeState,
): ThreadMessageLike[] {
  return withLifecycleMessages(
    agentMessages(
      {
        ...state,
        items: [
          ...presentationItems(state),
          ...(bridge?.commandErrors ?? []).map(
            (event): AcpItem => ({
              id: `command-error:${event.seq}:${event.requestId ?? event.command}`,
              seq: event.seq,
              role: 'assistant',
              text: '',
              error: event.error,
              origin: 'session_error',
            }),
          ),
        ].sort((left, right) => (left.seq ?? 0) - (right.seq ?? 0)),
      },
      bridge,
    ),
    bridge,
  )
}

/** Convert canonical entities to the existing rich conversation presentation. */
export function agentMessages(
  state: {
    items: AcpItem[]
    running: boolean
    toolResults: AcpState['toolResults']
    conversations?: AcpState['conversations']
  },
  bridge: BridgeState | undefined,
): ThreadMessageLike[] {
  // A provider can open its answer before streaming a separate thought entity.
  // Streaming follows the latest update, not the entity's original position.
  const latestByConversation = new Map<string, AcpItem>()
  for (const item of state.items) {
    const key = item.conversation?.sessionId ?? 'main'
    const latest = latestByConversation.get(key)
    if (
      !latest ||
      (item.updatedSeq ?? item.seq ?? 0) >=
        (latest.updatedSeq ?? latest.seq ?? 0)
    )
      latestByConversation.set(key, item)
  }
  return state.items.map((item) => {
    const tool = item.tool
    const conversationId = item.conversation?.sessionId
    const running = conversationId
      ? state.conversations?.[conversationId]?.state === 'running'
      : state.running
    const verified = tool ? state.toolResults[tool.id] : undefined
    const request = tool ? bridge?.permissionByToolUse[tool.id] : undefined
    const permission = request ? (bridge!.permissions[request] ?? null) : null
    // Correlated app results enrich output; ACP updates own the tool lifecycle.
    const status = tool?.status
    const result: ToolResultPayload | undefined = verified
      ? { ...verifiedResult(verified), isError: status === 'error' }
      : tool && (status === 'done' || status === 'error')
        ? {
            text: outputText(tool.output ?? tool.content),
            raw: tool.output ?? tool.content,
            isError: status === 'error',
          }
        : undefined
    const input = tool?.input
    const args =
      input && typeof input === 'object' && !Array.isArray(input)
        ? (input as Record<string, unknown>)
        : {}
    const name = tool?.name ?? kindRenderer[tool?.kind ?? ''] ?? 'AgentTool'
    const part: ToolPart | undefined = tool
      ? {
          kind: 'tool',
          toolUseId: tool.id,
          toolName: name,
          toolKind: name.startsWith('mcp__') ? 'mcp' : 'builtin',
          args,
          argsText:
            typeof input === 'string' ? input : JSON.stringify(input ?? {}),
          argsSettled: status !== 'pending',
          notes: [],
          result,
        }
      : undefined
    const artifact: ToolArtifact | undefined = tool
      ? {
          part: part!,
          permission,
          parentToolUseId: item.conversation?.parentToolCallId ?? null,
          status,
          title: tool.title,
          kind: tool.kind,
          content: tool.content,
          output: tool.output,
          locations: tool.locations,
          presentation:
            verified?.presentation ??
            tool.presentation ??
            declaredPresentation(name, bridge),
          startedTimestamp: item.startedTimestamp,
          finishedTimestamp: item.finishedTimestamp,
          appTool: appToolReference(verified),
        }
      : undefined
    const content: Exclude<ThreadMessageLike['content'], string> = tool
      ? [
          {
            type: 'tool-call',
            toolCallId: tool.id,
            toolName: name,
            args: args as Record<string, never>,
            argsText: part!.argsText,
            result,
            isError: result?.isError,
            artifact,
          },
        ]
      : item.reasoning !== undefined
        ? [{ type: 'reasoning', text: item.reasoning }]
        : item.displayContent
          ? visibleContent(item.displayContent)
          : [{ type: 'text', text: item.text }]
    return {
      id: item.id,
      role: item.role,
      ...(item.timestamp ? { createdAt: new Date(item.timestamp * 1000) } : {}),
      content,
      metadata: {
        custom: {
          kind: tool
            ? 'tools'
            : item.reasoning !== undefined
              ? 'reasoning'
              : 'text',
          timestamp: item.timestamp,
          updatedTimestamp: item.updatedTimestamp,
          finishedTimestamp: item.finishedTimestamp,
          sequence: item.seq,
          actor: item.actor,
          conversation: item.conversation,
          origin: item.origin,
          sendMode: item.sendMode,
          interaction: item.interaction,
          requestId: item.requestId,
          isReplay: false,
          // Canonical user entities are persisted acceptance receipts.
          delivered: item.role === 'user',
          protocol: 'acp',
          protocolVersion: 2,
          content: item.displayContent,
        },
      },
      ...(item.role === 'assistant'
        ? {
            status: item.error
              ? {
                  type: 'incomplete' as const,
                  reason: 'error' as const,
                  error: item.error,
                }
              : permission && !permission.resolved
                ? {
                    type: 'requires-action' as const,
                    reason: 'tool-calls' as const,
                  }
                : tool
                  ? status === 'pending' || status === 'running'
                    ? { type: 'running' as const }
                    : status === 'cancelled'
                      ? {
                          type: 'incomplete' as const,
                          reason: 'cancelled' as const,
                        }
                      : { type: 'complete' as const, reason: 'stop' as const }
                  : running &&
                      item ===
                        latestByConversation.get(conversationId ?? 'main') &&
                      item.finishedTimestamp === undefined
                    ? { type: 'running' as const }
                    : { type: 'complete' as const, reason: 'stop' as const },
          }
        : {}),
    }
  })
}

/** Preserve structured content while coalescing adjacent text chunks for display. */
function visibleContent(
  blocks: Record<string, any>[],
): Exclude<ThreadMessageLike['content'], string> {
  const content: Exclude<ThreadMessageLike['content'], string>[number][] = []
  const appendText = (text: string) => {
    const previous = content.at(-1)
    if (previous?.type === 'text')
      content[content.length - 1] = { ...previous, text: previous.text + text }
    else content.push({ type: 'text', text })
  }
  for (const block of blocks) {
    if (block.type === 'text') appendText(String(block.text ?? ''))
    else if (
      block.type === 'image' &&
      typeof block.mimeType === 'string' &&
      /^image\/(png|jpeg|gif|webp)$/.test(block.mimeType) &&
      typeof block.data === 'string'
    )
      content.push({
        type: 'image',
        image: `data:${block.mimeType};base64,${block.data}`,
      })
    else if (
      block.type === 'audio' &&
      typeof block.data === 'string' &&
      typeof block.mimeType === 'string'
    )
      content.push({
        type: 'file',
        filename: 'Audio',
        mimeType: block.mimeType,
        data: block.data,
      })
    else if (block.type === 'resource_link')
      appendText(
        `\n${String(block.title ?? block.name ?? 'Resource')}: ${String(block.uri ?? '')}\n`,
      )
    else if (block.type === 'resource') {
      const resource = object(block.resource)
      if (typeof resource.text === 'string') appendText(`\n${resource.text}\n`)
      else if (typeof resource.blob === 'string')
        content.push({
          type: 'file',
          filename: String(resource.uri ?? 'Resource'),
          mimeType: String(resource.mimeType ?? 'application/octet-stream'),
          data: resource.blob,
        })
    } else appendText(`\n[${String(block.type ?? 'Content')} content]\n`)
  }
  return content
}
